import { Button, Checkbox, Input, message, Select, Space } from "antd";
import { useState } from "react";
import { Flex } from "src/components/Flex";
import { DeepLinkType, SnippetV1State } from "pages/api/deep-links/types";
import { ResourceAccessRadioButtons } from "src/components/ResourceAccessRadioButtons";
import { useDeepLinks } from "src/hooks/useDeepLinks";
import { useApplicationContext } from "src/hooks/useApplication";
import { asKey } from "src/utils/fields";
import { mono } from "src/fonts";
import { SearchState } from "../types";
import {
  isColumnFormulaClause,
  isCompareWithInputClause,
  isDateRangeInputClause,
  isFilterConditionInputClause,
  isFormulaInputClause,
  isGroupByClause,
  isMeasureByClause,
  isSearchInputClause,
  isSelectInputClause,
  UseQuestionInputClauses,
} from "./inputs/question-input/definitions";

export function SnippetForm(props: {
  clauses: UseQuestionInputClauses["list"];
  onFinish(): void;
}) {
  const [saving, setSaving] = useState(false);
  const snippets = useDeepLinks(DeepLinkType.SNIPPET);
  const [isForCards, setIsForCards] = useState(false);
  const [tags, setTags] = useState<string[]>([]);
  const [group, setGroup] = useState("");
  const [name, setName] = useState("");
  const [scope, setScope] = useState<"private" | "workspace" | "global">(
    "private"
  );
  const allTags =
    snippets.links.data?.reduce((a, c) => {
      const json = c.json as SnippetV1State | undefined;
      return [...a, ...(json?.tags || [])];
    }, [] as string[]) || [];
  const allFolders =
    snippets.links.data?.reduce(
      (a, c) => (c.group ? [...a, c.group] : a),
      [] as string[]
    ) || [];
  const app = useApplicationContext();
  const isAtLeastSuper = app.user?.superuser || app.role === "super";
  const isAtLeastAdmin = isAtLeastSuper || app.role === "admin";
  const canEditShare = isAtLeastSuper || (isAtLeastAdmin && scope !== "global");
  const [clauseSelectionKeys, setClauseKeys] = useState(
    props.clauses.map((_, i) => i)
  );
  const params: SearchState = {} as SearchState;
  props.clauses.forEach((clause, idx) => {
    if (!clauseSelectionKeys.includes(idx)) return;
    else if (isSearchInputClause(clause) && clause.value)
      params.search = clause.value;
    else if (isSelectInputClause(clause) && clause.value)
      params.select = [...(params.select || []), clause.value];
    else if (isGroupByClause(clause) && clause.value)
      params.groupBys = [...(params.groupBys || []), clause.value];
    else if (isMeasureByClause(clause) && clause.value)
      params.aggregations = [...(params.aggregations || []), clause.value];
    else if (isFilterConditionInputClause(clause) && clause.value)
      params.filters = [...(params.filters || []), clause.value];
    else if (isColumnFormulaClause(clause) && clause.value)
      params.formulas = [...(params.formulas || []), clause.value];
    else if (isCompareWithInputClause(clause) && clause.value)
      params.compareWith = { ...params.compareWith, ...clause.value };
  });
  const state: SnippetV1State = { params, isForCards, tags };
  return (
    <div>
      <div style={{ fontWeight: 500, fontSize: 18, marginBottom: 16 }}>
        Create snippet from query
      </div>
      <Flex gap={8}>
        <div style={{ flex: 2 }}>
          <span style={{ fontSize: 15, fontWeight: 500 }}>Folder</span>
          <Select
            disabled={saving}
            style={{ minWidth: 200 }}
            placeholder="Folder name"
            mode="tags"
            value={group ? [group] : undefined}
            maxTagCount={2}
            options={[...new Set(allFolders)].map((folder) => ({
              value: folder,
              label: folder,
            }))}
            onChange={(values) => setGroup(values.pop() || "")}
          />
        </div>
        <div style={{ flex: 5 }}>
          <span style={{ fontSize: 15, fontWeight: 500 }}>Name</span>
          <Input
            value={name}
            status={name ? undefined : "error"}
            placeholder="Name the snippet"
            disabled={saving}
            onChange={(e) => {
              setName(e.target.value);
            }}
          />
        </div>
      </Flex>
      <div style={{ marginBottom: 16, marginTop: 16 }}>
        <span style={{ fontSize: 15, fontWeight: 500 }}>
          Select clauses to include:
        </span>
        <div style={{ padding: 6 }}>
          {props.clauses.map((clause, idx) =>
            isDateRangeInputClause(clause) ? null : (
              <div key={idx}>
                <Checkbox
                  disabled={saving}
                  checked={clauseSelectionKeys.includes(idx)}
                  onChange={(e) => {
                    setClauseKeys((keys_) =>
                      e.target.checked
                        ? [...keys_, idx]
                        : keys_.filter((key_) => key_ !== idx)
                    );
                  }}
                  style={{
                    ...mono.style,
                    maxWidth: "100%",
                    overflow: "hidden",
                  }}
                >
                  {isSelectInputClause(clause) ? (
                    <span>select by: {clause.value}</span>
                  ) : isSearchInputClause(clause) ? (
                    <span>search: {clause.value}</span>
                  ) : isGroupByClause(clause) && clause.value ? (
                    <span>group by: {asKey(clause.value)}</span>
                  ) : isMeasureByClause(clause) && clause.value ? (
                    <span>measure by: {asKey(clause.value)}</span>
                  ) : isFormulaInputClause(clause) && clause.value ? (
                    <span
                      style={{
                        display: "block",
                        whiteSpace: "nowrap",
                        maxWidth: 440,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      formula: {clause.value.name} (
                      {clause.value.formula.join(",")})
                    </span>
                  ) : isCompareWithInputClause(clause) && clause.value ? (
                    <span>compare with: {clause.value.ago}</span>
                  ) : isFilterConditionInputClause(clause) && clause.value ? (
                    <span
                      style={{
                        display: "block",
                        whiteSpace: "nowrap",
                        maxWidth: 440,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      filter by: {asKey(clause.value)}{" "}
                      {Object.keys(clause.value.comparison).at(0)}{" "}
                      {JSON.stringify(
                        Object.values(clause.value.comparison).at(0)
                      )}
                    </span>
                  ) : (
                    JSON.stringify(clause.value)
                  )}
                </Checkbox>
              </div>
            )
          )}
        </div>
      </div>
      <Flex
        style={{
          justifyContent: "flex-start",
          marginTop: 16,
          marginBottom: 16,
        }}
        gap={6}
      >
        <Select
          disabled={!isForCards || saving}
          style={{ minWidth: 200 }}
          placeholder="Pivot tags"
          mode="tags"
          value={tags}
          options={[...new Set(allTags)].map((tag) => ({
            value: tag,
            label: tag,
          }))}
          onChange={(values) => setTags(values)}
        />
        <Checkbox
          disabled={saving}
          checked={isForCards}
          onChange={(e) => setIsForCards(e.target.checked)}
        >
          <span style={{ fontSize: 12 }}>Use as card pivot</span>
        </Checkbox>
      </Flex>
      <div>
        {canEditShare && (
          <ResourceAccessRadioButtons
            isAtLeastSuper={isAtLeastSuper}
            type="snippet"
            disabled={saving}
            value={scope || "private"}
            onChange={(e) => setScope(e.target.value)}
          />
        )}
      </div>
      <Space
        style={{
          justifyContent: "flex-end",
          marginTop: 16,
          width: "100%",
        }}
      >
        <Button onClick={() => props.onFinish()}>Cancel</Button>
        <Button
          disabled={clauseSelectionKeys.length === 0 || !name}
          type="primary"
          loading={saving}
          onClick={() => {
            setSaving(true);
            snippets.create
              .mutateAsync({ name, scope, group, state })
              .then((link) => {
                if (link) message.info(`Created snippet "${name}"`);
                else message.error("There was an error, please try again.");
                props.onFinish();
              });
          }}
        >
          Create Snippet
        </Button>
      </Space>
    </div>
  );
}
